import React, { useState, useEffect } from 'react';
import {
  Shield,
  Upload,
  CheckCircle,
  AlertCircle,
  Clock,
  Camera,
  FileText,
  User,
  MapPin,
  Phone,
  Calendar,
  Flag
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import { iosGet, iosPost } from '../utils/iosFetch';

const KYCSubmission = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [kycStatus, setKycStatus] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    dob: '',
    country: '',
    id_type: 'national_id',
    id_number: '',
    address: '',
    city: '',
    postal_code: '',
    phone: ''
  });
  const [idDocument, setIdDocument] = useState(null);
  const [selfie, setSelfie] = useState(null);

  const BACKEND_URL = '';

  useEffect(() => {
    fetchKYCStatus();
  }, []);

  const fetchKYCStatus = async () => {
    try {
      const response = await iosGet(`${BACKEND_URL}/api/kyc/status`);

      if (response.ok) {
        const data = await response.json();
        setKycStatus(data);
      }
    } catch (error) {
      console.error('Error fetching KYC status:', error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5000000) {
        toast({
          title: 'Error',
          description: 'File size must be less than 5MB',
          variant: 'destructive'
        });
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'id') {
          setIdDocument(reader.result);
        } else {
          setSelfie(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      // Validate required fields
      if (!formData.full_name || !formData.dob || !formData.country || !formData.id_number) {
        toast({
          title: 'Error',
          description: 'Please fill all required fields',
          variant: 'destructive'
        });
        setLoading(false);
        return;
      }

      const response = await iosPost(`${BACKEND_URL}/api/kyc/submit`, formData);

      const data = await response.json();

      if (data.success || response.ok) {
        toast({
          title: '✅ KYC Submitted Successfully!',
          description: 'Your verification has been submitted. You will receive a notification once reviewed.',
          duration: 5000
        });
        
        // Show additional success toast
        setTimeout(() => {
          toast({
            title: '📧 Email Sent',
            description: 'Check your email for submission confirmation.',
            duration: 4000
          });
        }, 1000);
        
        fetchKYCStatus();
        setTimeout(() => navigate('/profile'), 3000);
      } else {
        throw new Error(data.detail || 'Submission failed');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit KYC',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusDisplay = () => {
    if (!kycStatus) return null;

    const statusConfig = {
      not_submitted: {
        icon: AlertCircle,
        color: 'orange',
        title: 'KYC Not Submitted',
        message: 'Complete KYC verification to unlock full platform features'
      },
      pending: {
        icon: Clock,
        color: 'yellow',
        title: 'KYC Under Review',
        message: 'Your KYC verification is being reviewed by our team'
      },
      approved: {
        icon: CheckCircle,
        color: 'green',
        title: 'KYC Verified',
        message: 'Your account is fully verified'
      },
      rejected: {
        icon: AlertCircle,
        color: 'red',
        title: 'KYC Rejected',
        message: kycStatus.kyc_message || 'Please resubmit with correct documents'
      }
    };

    const config = statusConfig[kycStatus.kyc_status] || statusConfig.not_submitted;
    const Icon = config.icon;

    return (
      <div className={`bg-${config.color}-900/20 border-2 border-${config.color}-500/50 rounded-xl p-6 mb-6`}>
        <div className="flex items-start gap-4">
          <Icon className={`w-8 h-8 text-${config.color}-400 flex-shrink-0 mt-1`} />
          <div className="flex-1">
            <h3 className={`text-xl font-bold text-${config.color}-400 mb-2`}>{config.title}</h3>
            <p className={`text-${config.color}-300`}>{config.message}</p>
            
            {kycStatus.kyc_status === 'approved' && (
              <button
                onClick={() => navigate('/profile')}
                className="mt-4 px-6 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-white font-semibold"
              >
                Back to Profile
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Don't show form if already approved
  if (kycStatus?.kyc_status === 'approved') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
        <div className="max-w-4xl mx-auto">
          {getStatusDisplay()}
        </div>
      </div>
    );
  }

  // Show status view if pending
  if (kycStatus?.kyc_status === 'pending') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
              <Shield className="w-8 h-8 text-cyan-400" />
              KYC Verification Status
            </h1>
            <p className="text-cyan-400">Your KYC verification is currently under review</p>
          </div>

          {getStatusDisplay()}

          {/* Show submitted data */}
          {kycStatus.kyc_data && (
            <div className="mt-6 bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
              <h2 className="text-xl font-bold text-white mb-4">Submitted Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-cyan-400">Full Name:</span>
                  <p className="text-white font-semibold">{kycStatus.kyc_data.full_name}</p>
                </div>
                <div>
                  <span className="text-cyan-400">Date of Birth:</span>
                  <p className="text-white font-semibold">{kycStatus.kyc_data.dob}</p>
                </div>
                <div>
                  <span className="text-cyan-400">Country:</span>
                  <p className="text-white font-semibold">{kycStatus.kyc_data.country}</p>
                </div>
                <div>
                  <span className="text-cyan-400">Phone:</span>
                  <p className="text-white font-semibold">{kycStatus.kyc_data.phone}</p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 flex gap-4">
            <button
              onClick={() => navigate('/profile')}
              className="flex-1 px-6 py-3 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-semibold"
            >
              Back to Profile
            </button>
            <button
              onClick={() => navigate('/')}
              className="flex-1 px-6 py-3 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white font-semibold"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6 pb-24">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Shield className="w-8 h-8 text-cyan-400" />
            KYC Verification
          </h1>
          <p className="text-cyan-400">Complete your identity verification to unlock all platform features</p>
        </div>

        {/* Current Status */}
        {getStatusDisplay()}

        {/* KYC Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-cyan-400" />
              Personal Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-cyan-400 text-sm font-semibold mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-cyan-400 text-sm font-semibold mb-2">
                  Date of Birth *
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-cyan-400" />
                  <input
                    type="date"
                    name="dob"
                    value={formData.dob}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-12 pr-4 py-3 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
                  />
                </div>
              </div>

              <div>
                <label className="block text-cyan-400 text-sm font-semibold mb-2">
                  Country *
                </label>
                <div className="relative">
                  <Flag className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-cyan-400" />
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-12 pr-4 py-3 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
                    placeholder="United States"
                  />
                </div>
              </div>

              <div>
                <label className="block text-cyan-400 text-sm font-semibold mb-2">
                  Phone Number *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-cyan-400" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-12 pr-4 py-3 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
                    placeholder="+1 234 567 8900"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ID Document Information */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-cyan-400" />
              ID Document
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-cyan-400 text-sm font-semibold mb-2">
                  ID Type *
                </label>
                <select
                  name="id_type"
                  value={formData.id_type}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
                >
                  <option value="national_id">National ID</option>
                  <option value="passport">Passport</option>
                  <option value="drivers_license">Driver's License</option>
                </select>
              </div>

              <div>
                <label className="block text-cyan-400 text-sm font-semibold mb-2">
                  ID Number *
                </label>
                <input
                  type="text"
                  name="id_number"
                  value={formData.id_number}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
                  placeholder="ID123456789"
                />
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-cyan-400" />
              Address
            </h2>
            
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-cyan-400 text-sm font-semibold mb-2">
                  Street Address *
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
                  placeholder="123 Main Street"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-cyan-400 text-sm font-semibold mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
                    placeholder="New York"
                  />
                </div>

                <div>
                  <label className="block text-cyan-400 text-sm font-semibold mb-2">
                    Postal Code *
                  </label>
                  <input
                    type="text"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
                    placeholder="10001"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Terms */}
          <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-4">
            <p className="text-blue-300 text-sm">
              <strong>Note:</strong> By submitting this form, you confirm that all information provided is accurate and truthful. 
              False information may result in account suspension.
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/profile')}
              className="flex-1 px-6 py-4 rounded-lg bg-gray-600 hover:bg-gray-700 text-white font-semibold transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-4 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-semibold transition-all disabled:opacity-50"
            >
              {loading ? 'Submitting...' : 'Submit KYC Verification'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default KYCSubmission;
